<template>
  <div ref="shell" class="vision-scope vision-chart-shell" :class="`vision-chart-shell--${mode}`">
    <div v-if="shellTitle" class="vision-chart-shell__header">
      <span class="vision-eyebrow">{{ shellTitle }}</span>
      <span v-if="shellSubtitle" class="vision-chart-shell__subtitle">{{ shellSubtitle }}</span>
    </div>

    <div v-if="isImageMode" class="detect">
      <figure class="detect-col">
        <figcaption class="detect-col__title">
          <v-icon size="16" color="#4676D4">mdi-eye-outline</v-icon>
          Sua leitura
        </figcaption>

        <div class="detect-strip">
          <div class="detect-strip__media">
            <img v-if="genStripSrc" :src="genStripSrc" class="detect-strip__img" alt="Tira analisada na vertical" />
            <div
              v-for="iv in sortedIntervals"
              :key="`band-${iv.name}`"
              class="detect-strip__band"
              :style="bandStyle(iv)"
            >
              <span class="detect-strip__badge">{{ iv.name }}</span>
            </div>
          </div>

          <div v-if="stripRows.length" class="strip-rows">
            <article
              v-for="row in stripRows"
              :key="`row-${row.name}`"
              :class="[
                'strip-row',
                row.reading ? `is-${row.reading.result}` : 'is-control',
              ]"
            >
              <span class="strip-row__connector" aria-hidden="true"></span>

              <header class="strip-row__head">
                <strong class="strip-row__name">{{ row.name }}</strong>
                <span v-if="row.reading" class="strip-row__status">
                  {{ row.reading.result }}
                </span>
                <span v-else class="strip-row__status strip-row__status--ctrl">controle</span>
                <span v-if="row.reading" class="strip-row__value">{{ formatReading(row.reading.avg) }}</span>
              </header>

              <template v-if="row.reading">
                <div class="strip-row__bar" :title="`Intensidade média: ${formatReading(row.reading.avg)}`">
                  <div class="strip-row__fill" :style="{ width: `${barWidth(row.reading.avg)}%` }"></div>
                </div>
                <div class="strip-row__sub">
                  <span><span class="strip-row__dot strip-row__dot--rgb"></span>RGB {{ formatReading(row.reading.rgb) }}</span>
                  <span><span class="strip-row__dot strip-row__dot--lum"></span>Lum {{ formatReading(row.reading.lum) }}</span>
                </div>
              </template>
              <p v-else class="strip-row__hint">Linha de referência da leitura</p>
            </article>
          </div>

          <div v-else class="reads-empty">
            <v-icon size="22" color="#9ca3af">mdi-test-tube-empty</v-icon>
            <span>Sem linhas calculadas para esta leitura.</span>
          </div>
        </div>
      </figure>
    </div>

    <canvas v-show="!isImageMode" ref="canvas" class="vision-chart-canvas"></canvas>

    <!-- <div v-if="peakChips.length" class="vision-chart-peaks">
      <span class="vision-chart-peaks__label">Picos detectados:</span>
      <span
        v-for="peak in peakChips"
        :key="peak.name"
        class="vision-chart-peaks__chip"
      >
        <span class="vision-chart-peaks__dot" aria-hidden="true"></span>
        <strong>{{ peak.name }}</strong>
        <span class="vision-chart-peaks__amp">amp {{ peak.amplitude }}</span>
      </span>
    </div> -->

    <v-alert v-if="renderError" dense text type="error" class="vision-chart-shell__error mt-3 mb-0">
      {{ renderError }}
    </v-alert>
  </div>
</template>

<script>
import { formatRatio, getVisionLineResult } from "./visionUtils";

const MARGIN_BASE = { top: 30, right: 24, bottom: 44, left: 64 };
const CURVE_PANEL_H = 220;
const CURVE_PANEL_H_COMPACT = 180;
const GAP = 36;
const PCT_TICKS = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0];
const PCT_TICKS_COMPACT = [0, 0.25, 0.5, 0.75, 1.0];
const Y_MAX = 255;
const FONT = '12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const FONT_BOLD = 'bold 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const FONT_TITLE = 'bold 14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const FONT_PEAK = 'bold 11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

const INK = "#1f2937";
const INK_SOFT = "#4b5563";
const GRID = "#cbd5e1";
const GRID_SOFT = "rgba(148, 163, 184, 0.35)";
const ACCENT_BLUE = "#4676D4";
const PEAK_COLOR = "#dc3545";

function preloadImage(dataUri) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUri;
  });
}

function drawPercentageTicks(ctx, x, y, width, showLabel, compact) {
  const ticks = compact ? PCT_TICKS_COMPACT : PCT_TICKS;
  ctx.font = FONT;
  ctx.fillStyle = INK_SOFT;
  ctx.textAlign = "center";

  for (const p of ticks) {
    const tx = x + p * width;
    const label = `${Math.round(p * 100)}%`;

    ctx.beginPath();
    ctx.strokeStyle = GRID;
    ctx.lineWidth = 1;
    ctx.moveTo(tx, y);
    ctx.lineTo(tx, y + 5);
    ctx.stroke();

    ctx.fillText(label, tx, y + 18);
  }

  if (showLabel) {
    ctx.font = FONT;
    ctx.fillStyle = INK_SOFT;
    ctx.textAlign = "center";
    ctx.fillText("% da largura", x + width / 2, y + 36);
  }
}

function drawYAxis(ctx, x, y, height, label, compact) {
  const steps = compact ? [0, 100, 200, 255] : [0, 50, 100, 150, 200, 255];

  ctx.font = FONT;
  ctx.fillStyle = INK_SOFT;
  ctx.textAlign = "right";

  for (const v of steps) {
    const py = y + height - (v / Y_MAX) * height;

    ctx.beginPath();
    ctx.strokeStyle = GRID;
    ctx.lineWidth = 1;
    ctx.moveTo(x - 5, py);
    ctx.lineTo(x, py);
    ctx.stroke();

    ctx.fillText(String(v), x - 8, py + 4);
  }

  ctx.save();
  ctx.translate(x - (compact ? 36 : 48), y + height / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.font = FONT;
  ctx.fillStyle = INK_SOFT;
  ctx.textAlign = "center";
  ctx.fillText(label, 0, 0);
  ctx.restore();
}

function drawGrid(ctx, x, y, width, height, compact) {
  const ySteps = compact ? [0, 100, 200, 255] : [0, 50, 100, 150, 200, 255];
  const ticks = compact ? PCT_TICKS_COMPACT : PCT_TICKS;

  ctx.save();
  ctx.strokeStyle = GRID_SOFT;
  ctx.lineWidth = 0.5;

  for (const v of ySteps) {
    const py = y + height - (v / Y_MAX) * height;
    ctx.beginPath();
    ctx.moveTo(x, py);
    ctx.lineTo(x + width, py);
    ctx.stroke();
  }

  for (const p of ticks) {
    const px = x + p * width;
    ctx.beginPath();
    ctx.moveTo(px, y);
    ctx.lineTo(px, y + height);
    ctx.stroke();
  }

  ctx.restore();
}

function drawPanelBorder(ctx, x, y, width, height) {
  ctx.strokeStyle = "#e5e7eb";
  ctx.lineWidth = 1;
  ctx.strokeRect(x, y, width, height);
}

function drawTitle(ctx, x, y, width, title) {
  ctx.font = FONT_TITLE;
  ctx.fillStyle = INK;
  ctx.textAlign = "left";
  ctx.fillText(title, x, y - 10);
}

function plotCurve(ctx, data, panelX, panelY, panelH, plotW, imgWidth, color, lineWidth) {
  if (!Array.isArray(data) || !data.length) return;

  const xScale = plotW / imgWidth;
  ctx.beginPath();
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;

  for (let i = 0; i < data.length; i++) {
    const px = panelX + i * xScale;
    const py = panelY + panelH - (data[i] / Y_MAX) * panelH;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }

  ctx.stroke();
}

function drawLegend(ctx, x, y, items) {
  const lineLen = 16;
  const spacing = 18;
  const padding = 8;
  const rectH = 22;

  ctx.font = FONT;
  const totalW = items.reduce((sum, item) => {
    return sum + lineLen + 8 + ctx.measureText(item.label).width + spacing;
  }, 0) - spacing + padding * 2;

  ctx.fillStyle = "rgba(255,255,255,0.92)";
  ctx.strokeStyle = "#e5e7eb";
  ctx.lineWidth = 1;
  ctx.beginPath();
  if (ctx.roundRect) {
    ctx.roundRect(x - padding, y - rectH / 2, totalW, rectH, 6);
  } else {
    ctx.rect(x - padding, y - rectH / 2, totalW, rectH);
  }
  ctx.fill();
  ctx.stroke();

  let cx = x;
  for (const item of items) {
    ctx.beginPath();
    ctx.strokeStyle = item.color;
    ctx.lineWidth = 2.5;
    ctx.moveTo(cx, y);
    ctx.lineTo(cx + lineLen, y);
    ctx.stroke();

    ctx.font = FONT;
    ctx.fillStyle = INK;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(item.label, cx + lineLen + 6, y);

    cx += lineLen + 8 + ctx.measureText(item.label).width + spacing;
  }
  ctx.textBaseline = "alphabetic";
}

function drawPeakMarker(ctx, px, panelTop, panelH, color) {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.setLineDash([4, 3]);
  ctx.beginPath();
  ctx.moveTo(px, panelTop);
  ctx.lineTo(px, panelTop + panelH);
  ctx.stroke();
  ctx.restore();
}

function drawPeakDot(ctx, px, py, color) {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(px, py, 4, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#fff";
  ctx.lineWidth = 1.5;
  ctx.stroke();
}

function drawPeakLabel(ctx, px, panelTop, name) {
  const padX = 6;
  ctx.font = FONT_PEAK;
  const textW = ctx.measureText(name).width;
  const w = textW + padX * 2;
  const h = 18;
  const x = px - w / 2;
  const y = panelTop - h - 4;

  ctx.fillStyle = PEAK_COLOR;
  ctx.beginPath();
  if (ctx.roundRect) {
    ctx.roundRect(x, y, w, h, 4);
  } else {
    ctx.rect(x, y, w, h);
  }
  ctx.fill();

  ctx.fillStyle = "#fff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(name, px, y + h / 2);
  ctx.textBaseline = "alphabetic";
}

function drawImagePanel(ctx, img, x, y, plotW, plotH, intervals, compact) {
  drawTitle(ctx, x, y, plotW, "Área de detecção");
  ctx.drawImage(img, x, y, plotW, plotH);

  for (const interval of intervals) {
    if (interval.min === null || interval.max === null) continue;

    const xStart = x + (interval.min / 100) * plotW;
    const xEnd = x + (interval.max / 100) * plotW;

    ctx.fillStyle = "rgba(70, 118, 212, 0.16)";
    ctx.fillRect(xStart, y, xEnd - xStart, plotH);

    ctx.font = FONT_BOLD;
    ctx.fillStyle = ACCENT_BLUE;
    ctx.textAlign = "center";
    ctx.fillText(interval.name, (xStart + xEnd) / 2, y + plotH * 0.08 + 14);
  }

  drawPanelBorder(ctx, x, y, plotW, plotH);
  drawPercentageTicks(ctx, x, y + plotH, plotW, false, compact);
}

function drawRGBPanel(ctx, colAvgs, peaks, x, y, plotW, plotH, imgWidth, compact) {
  drawTitle(ctx, x, y, plotW, "RGB por coluna");
  drawPanelBorder(ctx, x, y, plotW, plotH);
  drawGrid(ctx, x, y, plotW, plotH, compact);
  drawYAxis(ctx, x, y, plotH, "Intensidade", compact);

  plotCurve(ctx, colAvgs.r, x, y, plotH, plotW, imgWidth, "#e53935", 1.2);
  plotCurve(ctx, colAvgs.g, x, y, plotH, plotW, imgWidth, "#43a047", 1.2);
  plotCurve(ctx, colAvgs.b, x, y, plotH, plotW, imgWidth, "#1e88e5", 1.2);

  drawLegend(ctx, Math.max(x + 12, x + plotW - 180), y + 14, [
    { color: "#e53935", label: "Red" },
    { color: "#43a047", label: "Green" },
    { color: "#1e88e5", label: "Blue" },
  ]);

  if (Array.isArray(peaks)) {
    const xScale = plotW / imgWidth;
    for (const peak of peaks) {
      if (!peak) continue;
      const px = x + peak.col * xScale;
      const py = y + plotH - (peak.value / Y_MAX) * plotH;
      drawPeakMarker(ctx, px, y, plotH, PEAK_COLOR);
      drawPeakDot(ctx, px, py, PEAK_COLOR);
      drawPeakLabel(ctx, px, y, peak.name);
    }
  }

  drawPercentageTicks(ctx, x, y + plotH, plotW, false, compact);
}

function drawLumPanel(ctx, colAvgs, peaks, x, y, plotW, plotH, imgWidth, compact) {
  drawTitle(ctx, x, y, plotW, "Luminância por coluna");
  drawPanelBorder(ctx, x, y, plotW, plotH);
  drawGrid(ctx, x, y, plotW, plotH, compact);
  drawYAxis(ctx, x, y, plotH, "Intensidade", compact);

  plotCurve(ctx, colAvgs.luminance, x, y, plotH, plotW, imgWidth, "#475569", 1.4);
  drawLegend(ctx, Math.max(x + 12, x + plotW - 130), y + 14, [
    { color: "#475569", label: "Luminância" },
  ]);

  if (Array.isArray(peaks)) {
    const xScale = plotW / imgWidth;
    for (const peak of peaks) {
      if (!peak) continue;
      const lumVal = colAvgs.luminance[peak.col];
      const px = x + peak.col * xScale;
      const py = y + plotH - (lumVal / Y_MAX) * plotH;
      drawPeakMarker(ctx, px, y, plotH, PEAK_COLOR);
      drawPeakDot(ctx, px, py, PEAK_COLOR);
      drawPeakLabel(ctx, px, y, peak.name);
    }
  }

  drawPercentageTicks(ctx, x, y + plotH, plotW, true, compact);
}

export default {
  name: "VisionChartCanvas",

  props: {
    result: {
      type: Object,
      required: true,
    },
    intervals: {
      type: Array,
      required: true,
    },
    mode: {
      type: String,
      default: "all",
      validator: (v) => ["all", "image", "charts"].includes(v),
    },
  },

  data() {
    return {
      renderError: "",
      renderHandle: null,
      renderToken: 0,
      resizeObserver: null,
      genStripSrc: "",
    };
  },

  computed: {
    isImageMode() {
      return this.mode === "image";
    },

    sortedIntervals() {
      const list = Array.isArray(this.intervals) ? this.intervals : [];
      return list
        .filter((iv) => iv && iv.min !== null && iv.min !== undefined && iv.max !== null && iv.max !== undefined)
        .map((iv) => ({ name: iv.name, min: Number(iv.min), max: Number(iv.max) }))
        .sort((a, b) => a.min - b.min);
    },

    readingAverages() {
      const rgb = (this.result && this.result.diagRgb && this.result.diagRgb.lines) || {};
      const lum = (this.result && this.result.diagLum && this.result.diagLum.lines) || {};

      // Ordena pelas linhas de teste (o controle não entra em diag.lines).
      const order = this.sortedIntervals
        .map((iv) => iv.name)
        .filter((name) => name in rgb || name in lum);
      const seen = new Set(order);
      for (const name of [...Object.keys(rgb), ...Object.keys(lum)]) {
        if (!seen.has(name)) {
          order.push(name);
          seen.add(name);
        }
      }

      return order.map((name) => {
        const rRatio = rgb[name] && Number.isFinite(Number(rgb[name].ratio)) ? Number(rgb[name].ratio) : null;
        const lRatio = lum[name] && Number.isFinite(Number(lum[name].ratio)) ? Number(lum[name].ratio) : null;
        const values = [rRatio, lRatio].filter((v) => v !== null);
        const avg = values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : null;
        const results = [rgb[name], lum[name]].filter(Boolean).map(getVisionLineResult);
        const result = results.length === 2 && results.every((value) => value === "positivo")
          ? "positivo"
          : "negativo";
        return { name, avg, rgb: rRatio, lum: lRatio, result };
      });
    },

    // Une cada faixa da tira ("Sua leitura") à sua intensidade calculada,
    // mantendo a ordem vertical da tira (topo → base). Faixas sem leitura
    // (ex.: o controle) ficam com `reading: null`.
    stripRows() {
      const reads = {};
      for (const r of this.readingAverages) reads[r.name] = r;

      const rows = this.sortedIntervals.map((iv) => ({
        name: iv.name,
        min: iv.min,
        max: iv.max,
        reading: reads[iv.name] || null,
      }));

      // Inclui leituras que porventura não tenham faixa configurada.
      const seen = new Set(rows.map((r) => r.name));
      for (const r of this.readingAverages) {
        if (!seen.has(r.name)) rows.push({ name: r.name, min: null, max: null, reading: r });
      }

      return rows;
    },

    shellTitle() {
      if (this.mode === "image") return "Área de detecção";
      if (this.mode === "charts") return "Curvas por coluna";
      return "";
    },

    shellSubtitle() {
      if (this.mode === "image") return "Faixas azuis indicam intervalos de leitura configurados";
      if (this.mode === "charts") return "RGB e luminância para cada coluna da tira";
      return "";
    },

    peakChips() {
      const peaks = (this.result && this.result.peaks) || [];
      if (this.mode === "charts") return [];
      return peaks
        .filter(Boolean)
        .map((peak) => ({
          name: peak.name,
          amplitude: Number(peak.amplitude || 0).toFixed(1),
        }));
    },
  },

  watch: {
    result: {
      deep: true,
      handler() {
        if (this.isImageMode) this.buildGenStrip();
        else this.scheduleRender();
      },
    },
    intervals: {
      deep: true,
      handler() {
        if (!this.isImageMode) this.scheduleRender();
      },
    },
  },

  mounted() {
    if (this.isImageMode) {
      this.buildGenStrip();
      return;
    }

    this.scheduleRender();

    if (window.ResizeObserver) {
      this.resizeObserver = new window.ResizeObserver(() => this.scheduleRender());
      this.resizeObserver.observe(this.$refs.shell);
    } else {
      window.addEventListener("resize", this.scheduleRender);
    }
  },

  beforeDestroy() {
    if (this.renderHandle) window.cancelAnimationFrame(this.renderHandle);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    else window.removeEventListener("resize", this.scheduleRender);
  },

  methods: {
    scheduleRender() {
      if (this.renderHandle) window.cancelAnimationFrame(this.renderHandle);
      this.renderHandle = window.requestAnimationFrame(() => {
        this.renderHandle = null;
        this.render();
      });
    },

    bandStyle(iv) {
      const top = Math.max(0, Math.min(100, iv.min));
      const height = Math.max(0, Math.min(100, iv.max) - top);
      return { top: `${top}%`, height: `${height}%` };
    },

    formatReading(value) {
      return formatRatio(value);
    },

    barWidth(ratio) {
      const value = Number(ratio);
      if (!Number.isFinite(value) || value <= 0) return 2;
      return Math.max(2, Math.min(100, value * 100));
    },

    async buildGenStrip() {
      if (!this.result || !this.result.imageDataUri) {
        this.genStripSrc = "";
        return;
      }

      try {
        const img = await preloadImage(this.result.imageDataUri);
        const w = img.width;
        const h = img.height;
        const canvas = document.createElement("canvas");

        // Gira 90° no sentido horário: a extremidade esquerda da tira (0%,
        // controle) vai para o topo, deixando a imagem na vertical.
        canvas.width = h;
        canvas.height = w;
        const ctx = canvas.getContext("2d");
        ctx.translate(h, 0);
        ctx.rotate(Math.PI / 2);
        ctx.drawImage(img, 0, 0, w, h);

        this.genStripSrc = canvas.toDataURL("image/png");
      } catch (err) {
        this.genStripSrc = "";
      }
    },

    async render() {
      if (this.isImageMode) return;
      if (!this.result || !this.result.imageDataUri || !this.$refs.canvas) return;

      const token = ++this.renderToken;
      this.renderError = "";

      try {
        const img = await preloadImage(this.result.imageDataUri);
        if (token !== this.renderToken) return;

        const canvas = this.$refs.canvas;
        const shellWidth = this.$refs.shell ? this.$refs.shell.clientWidth : 0;
        const innerPadding = 24;
        const canvasW = Math.max(320, Math.min(1188, Math.floor((shellWidth || 900) - innerPadding)));
        const compact = canvasW < 560;
        const margin = {
          ...MARGIN_BASE,
          left: compact ? 48 : MARGIN_BASE.left,
          right: compact ? 14 : MARGIN_BASE.right,
        };
        const curvePanelH = compact ? CURVE_PANEL_H_COMPACT : CURVE_PANEL_H;
        const plotW = Math.max(220, canvasW - margin.left - margin.right);
        const imgW = Number(this.result.width || img.width || plotW);
        const imgH = Number(this.result.height || img.height || 120);
        const imgPanelH = Math.max(compact ? 110 : 140, Math.min(350, (imgH / imgW) * plotW));

        const showImage = this.mode === "all" || this.mode === "image";
        const showCharts = this.mode === "all" || this.mode === "charts";

        let totalH = margin.top + margin.bottom;
        if (showImage) totalH += imgPanelH + 24;
        if (showImage && showCharts) totalH += GAP;
        if (showCharts) totalH += curvePanelH + 24 + GAP + curvePanelH + 24;

        const dpr = window.devicePixelRatio || 1;

        canvas.width = canvasW * dpr;
        canvas.height = totalH * dpr;
        canvas.style.width = `${canvasW}px`;
        canvas.style.height = `${totalH}px`;

        const ctx = canvas.getContext("2d");
        ctx.scale(dpr, dpr);
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, canvasW, totalH);

        const colAvgs = this.result.colAvgs || { r: [], g: [], b: [], luminance: [] };
        const peaks = this.result.peaks || [];

        let cursorY = margin.top;

        if (showImage) {
          drawImagePanel(ctx, img, margin.left, cursorY, plotW, imgPanelH, this.intervals, compact);
          cursorY += imgPanelH + 24;
          if (showCharts) cursorY += GAP;
        }

        if (showCharts) {
          drawRGBPanel(ctx, colAvgs, peaks, margin.left, cursorY, plotW, curvePanelH, imgW, compact);
          cursorY += curvePanelH + 24 + GAP;
          drawLumPanel(ctx, colAvgs, peaks, margin.left, cursorY, plotW, curvePanelH, imgW, compact);
        }
      } catch (err) {
        this.renderError = err && err.message ? err.message : "Não foi possível renderizar o gráfico.";
      }
    },
  },
};
</script>

<style>
@import "./visionDesignTokens.css";
</style>

<style scoped>
.vision-chart-shell {
  width: 100%;
  overflow-x: auto;
  background: var(--vision-surface);
  border: 1px solid var(--vision-border);
  border-radius: var(--vision-radius-md);
  padding: 14px 12px;
  box-shadow: var(--vision-shadow-1);
}

.vision-chart-shell--image {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-top: 0;
}

.vision-chart-shell__header {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 4px 10px;
}

.vision-chart-shell__subtitle {
  font-size: 12.5px;
  color: var(--vision-ink-5);
  font-weight: 500;
}

.vision-chart-canvas {
  display: block;
  max-width: 100%;
  margin: 0 auto;
  border-radius: var(--vision-radius-sm);
}

/* ===== Sua leitura (modo imagem) ===== */
.detect {
  display: flex;
  justify-content: center;
  padding: 6px 4px 2px;
}

.detect-col {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  max-width: 560px;
}

.detect-col__title {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--vision-ink-4);
}

.detect-strip {
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 18px;
  min-height: clamp(250px, 46vh, 380px);
}

.detect-strip__media {
  position: relative;
  align-self: stretch;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 62px;
  border-radius: var(--vision-radius-sm);
  overflow: hidden;
  background: var(--vision-surface-2);
  border: 1px solid var(--vision-border);
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.02);
}

.detect-strip__img {
  display: block;
  height: 100%;
  width: auto;
  object-fit: contain;
}

.detect-strip__band {
  position: absolute;
  left: 2px;
  right: 2px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background: transparent;
  border: 1.5px solid rgba(70, 118, 212, 0.85);
  border-radius: 4px;
  box-shadow: 0 0 0 1.5px rgba(255, 255, 255, 0.45);
  pointer-events: none;
}

.detect-strip__badge {
  opacity: 0.35;
  margin-top: 3px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.3px;
  line-height: 1;
  padding: 2px 6px;
  border-radius: var(--vision-radius-pill);
  color: #fff;
  background: rgba(70, 118, 212, 0.92);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.25);
  white-space: nowrap;
  max-width: calc(100% - 6px);
  overflow: hidden;
}

/* ===== Cards de intensidade (antes era a 2ª coluna) ===== */
.strip-rows {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
}

.strip-row {
  --row-color: var(--vision-success);
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 7px 11px;
  background: var(--vision-surface);
  border: 1px solid var(--vision-border);
  border-left: 3px solid var(--row-color);
  border-radius: var(--vision-radius-sm);
  box-shadow: var(--vision-shadow-1);
  transition: box-shadow var(--vision-anim-base) var(--vision-ease),
    transform var(--vision-anim-base) var(--vision-ease);
}

.strip-row:hover {
  box-shadow: var(--vision-shadow-2);
  transform: translateX(2px);
}

.strip-row.is-positivo {
  --row-color: var(--vision-danger);
}

.strip-row.is-inconclusivo {
  --row-color: var(--vision-warning);
}

.strip-row.is-control {
  --row-color: var(--vision-border-strong);
  background: var(--vision-surface-2);
  box-shadow: none;
}

/* Conector que liga o card à tira */
.strip-row__connector {
  position: absolute;
  left: -18px;
  top: 50%;
  width: 18px;
  height: 2px;
  border-radius: 2px;
  background: var(--row-color);
  opacity: 0.55;
}

.strip-row__head {
  display: flex;
  align-items: center;
  gap: 7px;
}

.strip-row__name {
  font-size: 12px;
  font-weight: 700;
  color: var(--vision-ink-2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.strip-row__status {
  flex: 0 0 auto;
  font-size: 8.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 1px 6px;
  border-radius: var(--vision-radius-pill);
  background: color-mix(in srgb, var(--row-color) 16%, white);
  color: var(--row-color);
}

.strip-row__status--ctrl {
  color: var(--vision-ink-5);
  background: var(--vision-surface-3);
}

.strip-row__value {
  margin-left: auto;
  font-family: var(--vision-font-mono);
  font-size: 12.5px;
  font-weight: 700;
  color: var(--vision-ink-1);
  line-height: 1;
}

.strip-row__bar {
  height: 5px;
  border-radius: var(--vision-radius-pill);
  background: var(--vision-surface-3);
  overflow: hidden;
}

.strip-row__fill {
  height: 100%;
  border-radius: var(--vision-radius-pill);
  background: linear-gradient(90deg, color-mix(in srgb, var(--row-color) 70%, white), var(--row-color));
  transition: width var(--vision-anim-slow) var(--vision-ease);
}

.strip-row__sub {
  display: flex;
  gap: 12px;
  font-size: 9.5px;
  color: var(--vision-ink-5);
  font-family: var(--vision-font-mono);
}

.strip-row__sub span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.strip-row__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.strip-row__dot--rgb {
  background: linear-gradient(135deg, #e53935 0%, #43a047 50%, #1e88e5 100%);
}

.strip-row__dot--lum {
  background: #475569;
}

.strip-row__hint {
  margin: 0;
  font-size: 10px;
  color: var(--vision-ink-5);
}

.reads-empty {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--vision-ink-5);
  font-size: 13.5px;
  padding: 12px 0;
}

@media (max-width: 560px) {
  .detect-strip {
    gap: 14px;
    min-height: clamp(230px, 50vh, 340px);
  }

  .detect-strip__media {
    width: clamp(46px, 16vw, 60px);
  }

  .strip-row {
    padding: 7px 10px;
  }

  .strip-row__connector {
    left: -14px;
    width: 14px;
  }
}

.vision-chart-peaks {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 0 4px;
}

.vision-chart-peaks__label {
  font-size: 12px;
  color: var(--vision-ink-5);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.vision-chart-peaks__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: var(--vision-primary-soft);
  border: 1px solid rgba(70, 118, 212, 0.22);
  border-radius: var(--vision-radius-pill);
  font-size: 12.5px;
  color: var(--vision-ink-2);
}

.vision-chart-peaks__chip strong {
  color: var(--vision-primary-dark);
  font-weight: 700;
}

.vision-chart-peaks__amp {
  color: var(--vision-ink-4);
  font-family: var(--vision-font-mono);
  font-size: 11.5px;
}

.vision-chart-peaks__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--vision-primary);
}

.vision-chart-shell__error {
  border-radius: var(--vision-radius-sm) !important;
}
</style>
